import { useState, useEffect } from "react";
import { API } from "../App";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Search, Plus, Laptop, Smartphone, Monitor, Printer, Tablet, Cpu } from "lucide-react";

const deviceCategories = [
  { value: "ordinateur_portable", label: "Ordinateur portable", icon: Laptop },
  { value: "ordinateur_fixe", label: "Ordinateur fixe", icon: Monitor },
  { value: "smartphone", label: "Smartphone", icon: Smartphone },
  { value: "tablette", label: "Tablette", icon: Tablet },
  { value: "imprimante", label: "Imprimante", icon: Printer },
  { value: "ecran", label: "Écran", icon: Monitor },
  { value: "autre", label: "Autre", icon: Cpu }
];

const accessoiresList = ["chargeur", "cable", "coque", "batterie", "carton", "sacoche", "autre"];

const DevicesPage = () => {
  const [devices, setDevices] = useState([]);
  const [clients, setClients] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    client_id: "",
    categorie: "",
    marque: "",
    modele: "",
    numero_serie: "",
    imei: "",
    capacite_stockage: "",
    couleur: "",
    accessoires_remis: [],
    etat_esthetique: "",
    ecran_casse: false,
    appareil_allume: true,
    mot_de_passe: "",
    panne_declaree: "",
    symptomes_constates: ""
  });

  const fetchDevices = async () => {
    setLoading(true);
    try {
      const params = { search, limit: 100 };
      if (categoryFilter) params.categorie = categoryFilter;
      const response = await API.get("/devices", { params });
      setDevices(response.data.devices);
      setTotal(response.data.total);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await API.get("/clients", { params: { limit: 200 } });
      setClients(response.data.clients);
    } catch (error) {
      console.error("Error fetching clients");
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    const timer = setTimeout(fetchDevices, 300);
    return () => clearTimeout(timer);
  }, [search, categoryFilter]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await API.post("/devices", formData);
      toast.success("Appareil enregistré");
      setDialogOpen(false);
      resetForm();
      fetchDevices();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  const resetForm = () => {
    setFormData({
      client_id: "",
      categorie: "",
      marque: "",
      modele: "",
      numero_serie: "",
      imei: "",
      capacite_stockage: "",
      couleur: "",
      accessoires_remis: [],
      etat_esthetique: "",
      ecran_casse: false,
      appareil_allume: true,
      mot_de_passe: "",
      panne_declaree: "",
      symptomes_constates: ""
    });
  };

  const toggleAccessoire = (acc) => {
    const current = formData.accessoires_remis;
    if (current.includes(acc)) {
      setFormData({ ...formData, accessoires_remis: current.filter(a => a !== acc) });
    } else {
      setFormData({ ...formData, accessoires_remis: [...current, acc] });
    }
  };

  const getCategoryIcon = (cat) => {
    const found = deviceCategories.find(c => c.value === cat);
    return found ? found.icon : Cpu;
  };

  return (
    <div className="space-y-6" data-testid="devices-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Appareils</h1>
          <p className="text-muted-foreground">{total} appareils enregistrés</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-device-btn">
              <Plus className="h-4 w-4 mr-2" />
              Nouvel appareil
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <form onSubmit={handleSubmit}>
              <DialogHeader>
                <DialogTitle>Nouvel appareil</DialogTitle>
                <DialogDescription>Enregistrez un nouvel appareil pour un client</DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                {/* Client Selection */}
                <div className="space-y-2">
                  <Label>Client *</Label>
                  <Select
                    value={formData.client_id}
                    onValueChange={(value) => setFormData({ ...formData, client_id: value })}
                    required
                  >
                    <SelectTrigger data-testid="device-client-select">
                      <SelectValue placeholder="Sélectionner un client" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map((client) => (
                        <SelectItem key={client.id} value={client.id}>
                          {client.prenom} {client.nom} - {client.telephone}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Category */}
                <div className="space-y-2">
                  <Label>Catégorie *</Label>
                  <Select
                    value={formData.categorie}
                    onValueChange={(value) => setFormData({ ...formData, categorie: value })}
                    required
                  >
                    <SelectTrigger data-testid="device-category-select">
                      <SelectValue placeholder="Sélectionner une catégorie" />
                    </SelectTrigger>
                    <SelectContent>
                      {deviceCategories.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Brand & Model */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Marque *</Label>
                    <Input
                      value={formData.marque}
                      onChange={(e) => setFormData({ ...formData, marque: e.target.value })}
                      placeholder="Ex: Apple, Samsung, HP..."
                      required
                      data-testid="device-brand-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Modèle *</Label>
                    <Input
                      value={formData.modele}
                      onChange={(e) => setFormData({ ...formData, modele: e.target.value })}
                      placeholder="Ex: iPhone 13, Galaxy S21..."
                      required
                      data-testid="device-model-input"
                    />
                  </div>
                </div>

                {/* Serial & IMEI */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Numéro de série</Label>
                    <Input
                      value={formData.numero_serie}
                      onChange={(e) => setFormData({ ...formData, numero_serie: e.target.value })}
                      data-testid="device-serial-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>IMEI (mobile)</Label>
                    <Input
                      value={formData.imei}
                      onChange={(e) => setFormData({ ...formData, imei: e.target.value })}
                      data-testid="device-imei-input"
                    />
                  </div>
                </div>

                {/* Storage & Color */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Capacité stockage</Label>
                    <Input
                      value={formData.capacite_stockage}
                      onChange={(e) => setFormData({ ...formData, capacite_stockage: e.target.value })}
                      placeholder="Ex: 128Go, 256Go..."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Couleur</Label>
                    <Input
                      value={formData.couleur}
                      onChange={(e) => setFormData({ ...formData, couleur: e.target.value })}
                      placeholder="Ex: Noir, Blanc..."
                    />
                  </div>
                </div>

                {/* Accessories */}
                <div className="space-y-2">
                  <Label>Accessoires remis</Label>
                  <div className="flex flex-wrap gap-2">
                    {accessoiresList.map((acc) => (
                      <Badge
                        key={acc}
                        variant={formData.accessoires_remis.includes(acc) ? "default" : "outline"}
                        className="cursor-pointer capitalize"
                        onClick={() => toggleAccessoire(acc)}
                      >
                        {acc}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Aesthetic state */}
                <div className="space-y-2">
                  <Label>État esthétique</Label>
                  <Select
                    value={formData.etat_esthetique}
                    onValueChange={(value) => setFormData({ ...formData, etat_esthetique: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner l'état" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="neuf">Neuf</SelectItem>
                      <SelectItem value="excellent">Excellent</SelectItem>
                      <SelectItem value="bon">Bon</SelectItem>
                      <SelectItem value="correct">Correct</SelectItem>
                      <SelectItem value="use">Usé</SelectItem>
                      <SelectItem value="casse">Cassé</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Switches */}
                <div className="flex flex-wrap gap-6">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={formData.ecran_casse}
                      onCheckedChange={(checked) => setFormData({ ...formData, ecran_casse: checked })}
                    />
                    <Label>Écran cassé</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={formData.appareil_allume}
                      onCheckedChange={(checked) => setFormData({ ...formData, appareil_allume: checked })}
                    />
                    <Label>Appareil s'allume</Label>
                  </div>
                </div>

                {/* Password */}
                <div className="space-y-2">
                  <Label>Mot de passe / Code / Schéma</Label>
                  <Input
                    value={formData.mot_de_passe}
                    onChange={(e) => setFormData({ ...formData, mot_de_passe: e.target.value })}
                    placeholder="Ou 'non communiqué'"
                  />
                </div>

                {/* Problem description */}
                <div className="space-y-2">
                  <Label>Panne déclarée *</Label>
                  <Textarea
                    value={formData.panne_declaree}
                    onChange={(e) => setFormData({ ...formData, panne_declaree: e.target.value })}
                    placeholder="Description du problème par le client"
                    required
                    data-testid="device-problem-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Symptômes constatés</Label>
                  <Textarea
                    value={formData.symptomes_constates}
                    onChange={(e) => setFormData({ ...formData, symptomes_constates: e.target.value })}
                    placeholder="Observations à la réception"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Annuler
                </Button>
                <Button type="submit" data-testid="device-submit-btn">Enregistrer</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher par marque, modèle, série, IMEI..."
                className="pl-10"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="device-search-input"
              />
            </div>
            <Select value={categoryFilter || "all"} onValueChange={(v) => setCategoryFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-full sm:w-48" data-testid="device-filter-select">
                <SelectValue placeholder="Toutes catégories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes catégories</SelectItem>
                {deviceCategories.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
            </div>
          ) : devices.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-center">
              <Laptop className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="font-medium">Aucun appareil trouvé</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Appareil</TableHead>
                    <TableHead>Catégorie</TableHead>
                    <TableHead className="hidden md:table-cell">N° Série / IMEI</TableHead>
                    <TableHead className="hidden lg:table-cell">Panne</TableHead>
                    <TableHead className="hidden md:table-cell">Date dépôt</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {devices.map((device) => {
                    const Icon = getCategoryIcon(device.categorie);
                    return (
                      <TableRow key={device.id} data-testid={`device-row-${device.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary">
                              <Icon className="h-5 w-5 text-muted-foreground" />
                            </div>
                            <div>
                              <p className="font-medium">{device.marque} {device.modele}</p>
                              {device.couleur && (
                                <p className="text-sm text-muted-foreground">{device.couleur}</p>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="capitalize">
                            {device.categorie?.replace(/_/g, " ")}
                          </Badge>
                        </TableCell>
                        <TableCell className="hidden md:table-cell font-mono text-sm">
                          {device.numero_serie || device.imei || "-"}
                        </TableCell>
                        <TableCell className="hidden lg:table-cell max-w-xs truncate">
                          {device.panne_declaree}
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          {new Date(device.date_depot).toLocaleDateString("fr-FR")}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DevicesPage;
